import { isDefined } from "@clipboard-health/util-ts";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import StarIcon from "@mui/icons-material/Star";
import { alpha, type Theme } from "@mui/material";
import { capitalizeFirstLetter } from "@src/appV2/lib/utils/capitalizeFirstLetter";
import { STARRED_PROFESSIONAL_COLOR } from "@src/appV2/Professionals/MyProfessionals/constants";
import { getCredentialsTypeSnippet } from "@src/appV2/WorkerCredentials/BulkShareCredentials/helpers";
import { addHours, differenceInMinutes, isAfter, parseISO } from "date-fns";
import pluralize from "pluralize";
import { createElement } from "react";

export function getWorkerTypeKey(workplaceId: string | undefined): string {
  return `${workplaceId ?? ""}-aTeamSelectedWorkerType`;
}

export function isShiftStartingSoon(
  shiftStartingSoonThresholdInHours: number,
  shiftStartDateTime: string | undefined
): shiftStartDateTime is string {
  return (
    isDefined(shiftStartDateTime) &&
    isAfter(addHours(new Date(), shiftStartingSoonThresholdInHours), parseISO(shiftStartDateTime))
  );
}

function getCredentialsNeededMessage(
  shiftStartDateTime: string,
  credentialsTypeSnippet: string
): string {
  const timeUntilShiftStartInMinutes = differenceInMinutes(
    parseISO(shiftStartDateTime),
    new Date()
  );
  const timeUntilShiftStartInHours = Math.ceil(timeUntilShiftStartInMinutes / 60);
  const credentialsPrefix = `${capitalizeFirstLetter(credentialsTypeSnippet)} credentials needed`;
  if (timeUntilShiftStartInHours <= 0) {
    return `${credentialsPrefix} \u00A0•\u00A0 Shift in progress`;
  }
  if (timeUntilShiftStartInHours === 1) {
    return `${credentialsPrefix} in ${timeUntilShiftStartInMinutes} ${pluralize(
      "minute",
      timeUntilShiftStartInMinutes
    )}`;
  }

  return `${credentialsPrefix} in ${timeUntilShiftStartInHours} ${pluralize(
    "hour",
    timeUntilShiftStartInHours
  )}`;
}

export function getStyleForProfessionalCard(props: {
  completedShifts: number;
  missingCredentials: boolean;
  activeTab: "upcoming" | "past";
  shiftStartDateTime: string | undefined;
  shiftStartingSoonThresholdInHours: number;
  theme: Theme;
  isStarred: boolean;
  isChartingOnly: boolean;
  isTimeClockOnly: boolean;
  isChartingAndTimeClock: boolean;
  isBlocked: boolean;
}) {
  const {
    completedShifts,
    missingCredentials,
    activeTab,
    shiftStartDateTime,
    shiftStartingSoonThresholdInHours,
    theme,
    isStarred,
    isChartingOnly,
    isTimeClockOnly,
    isChartingAndTimeClock,
    isBlocked,
  } = props;
  const shiftIsStartingSoon = isShiftStartingSoon(
    shiftStartingSoonThresholdInHours,
    shiftStartDateTime
  );
  const credentialsTypeSnippet = getCredentialsTypeSnippet(
    isChartingOnly || isChartingAndTimeClock,
    isTimeClockOnly || isChartingAndTimeClock
  );
  if (
    missingCredentials &&
    activeTab === "upcoming" &&
    shiftIsStartingSoon &&
    completedShifts === 0
  ) {
    return {
      headerBackgroundColor: alpha(theme.palette.warning.main, 0.7),
      headerText: `New to your workplace \u00A0•\u00A0 ${getCredentialsNeededMessage(
        shiftStartDateTime,
        credentialsTypeSnippet
      )}`,
      borderColor: alpha(theme.palette.warning.main, 0.7),
      cardBackgroundColor: alpha(theme.palette.warning.main, 0.1),
    };
  }

  if (isBlocked) {
    return {
      headerBackgroundColor: alpha(theme.palette.common.black, 0.5),
      headerText: "Blocked",
      headerIcon: createElement(BlockRoundedIcon, { fontSize: "inherit" }),
      borderColor: alpha(theme.palette.common.black, 0.5),
      cardBackgroundColor: alpha(theme.palette.common.black, 0.1),
    };
  }

  if (isStarred) {
    return {
      headerBackgroundColor: alpha(STARRED_PROFESSIONAL_COLOR, 0.7),
      headerText: "Starred Professional",
      headerIcon: createElement(StarIcon, { fontSize: "inherit" }),
      borderColor: alpha(STARRED_PROFESSIONAL_COLOR, 0.7),
      cardBackgroundColor: alpha(STARRED_PROFESSIONAL_COLOR, 0.1),
    };
  }

  return {
    borderColor: alpha(theme.palette.common.black, 0.2),
    cardBackgroundColor: undefined,
    headerBackgroundColor: undefined,
    headerText: undefined,
  };
}
